import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { fetchWsApprovals, WsApprovalRow } from "services/work-security-approvals.service";

/**
 * The per-sphere pareigybės approvals waiting on the CURRENT user, per company
 * (positions catalogue §5 GET work-security/approvals), for the «Tvarkos
 * tvirtinimai» queue page of the task list. The number on the bell and on the
 * «Laukia patvirtinimo» tab of Tvarkos comes from the cabinet summary instead.
 *
 * `companyId` is recorded with the data so rows from the previous company
 * never show under the next one.
 */
export interface WsApprovalsState {
  companyId: string | null;
  rows: WsApprovalRow[];
  /** True once `rows` holds the queue answer for `companyId`. */
  rowsLoaded: boolean;
  isLoading: boolean;
  isError: boolean;
}

const initialState: WsApprovalsState = {
  companyId: null,
  rows: [],
  rowsLoaded: false,
  isLoading: false,
  isError: false,
};

const wsApprovalsSlice = createSlice({
  name: "work-security-approvals",
  initialState,
  reducers: {
    resetWsApprovals() {
      return initialState;
    },
    setWsApprovalsLoading(state, action: PayloadAction<{ companyId: string }>) {
      if (state.companyId !== action.payload.companyId) {
        state.companyId = action.payload.companyId;
        state.rows = [];
        state.rowsLoaded = false;
      }
      state.isLoading = true;
      state.isError = false;
    },
    setWsApprovals(state, action: PayloadAction<{ companyId: string; rows: WsApprovalRow[] }>) {
      state.companyId = action.payload.companyId;
      state.rows = action.payload.rows;
      state.rowsLoaded = true;
      state.isLoading = false;
      state.isError = false;
    },
    setWsApprovalsError(state, action: PayloadAction<{ companyId: string }>) {
      state.companyId = action.payload.companyId;
      state.isLoading = false;
      state.isError = true;
    },
  },
});

const { actions, reducer } = wsApprovalsSlice;

export const {
  resetWsApprovals,
  setWsApprovalsLoading,
  setWsApprovals,
  setWsApprovalsError,
} = actions;

export default reducer;

// Only the LATEST request may write — a company switch mid-flight, or a
// post-approval reload racing an older fetch, must not leave the list at the
// previous answer.
let latestQueueRequest = 0;

/** The whole pending queue across the caller's spheres (the «Tvarkos tvirtinimai» queue page). */
export const fetchWsApprovalsQueue = (companyId: string) => async (dispatch: any) => {
  const requestId = ++latestQueueRequest;
  dispatch(setWsApprovalsLoading({ companyId }));
  try {
    const rows = await fetchWsApprovals(companyId, { status: "pending" });
    if (requestId !== latestQueueRequest) return;
    dispatch(setWsApprovals({ companyId, rows }));
  } catch (e) {
    if (requestId !== latestQueueRequest) return;
    dispatch(setWsApprovalsError({ companyId }));
  }
};

const WS_MODULE_PERMISSIONS = [
  "Master",
  "Access work security assignments",
  // Granted by auth/get-role to anyone who manages at least one sphere,
  // i.e. exactly the people who can be approvers.
  "Access work security module",
];

type RoleLike = { permissionsgroups?: string[] | null; company?: string | null } | null | undefined;

/**
 * True when this role can reach Tvarkos — the only people who can approve.
 * Gates the fetches so ordinary employees never fire a request per page load.
 * The role in the store is the LAST one fetched and is not cleared on a
 * company switch, so it must also be THIS company's.
 */
export function canSeeWsApprovalsFor(role: RoleLike, companyId?: string | null): boolean {
  if (!role || !companyId) return false;
  if (role.company && role.company !== companyId) return false;
  const groups = role.permissionsgroups || [];
  return WS_MODULE_PERMISSIONS.some((permission) => groups.includes(permission));
}
